import React from 'react';
import {
    Badge,
    Box,
    Button,
    Card,
    Group,
    Image,
    Progress,
    SimpleGrid,
    Stack,
    Text,
    Title,
    ThemeIcon,
} from '@mantine/core';
import { IconBolt, IconCheck, IconSpeakerphone, IconX } from '@tabler/icons-react';
import draftKingsLogo from '../../assets/partners/draftkings.png';

interface MarketplacePartner {
    id: string;
    name: string;
    logo: React.ReactNode;
    monetizeScore: number;
    usesMomentApi: boolean;
    accentColor: string;
}

const PARTNERS: MarketplacePartner[] = [
    {
        id: 'draftkings',
        name: 'DraftKings',
        logo: <Image src={draftKingsLogo} alt="DraftKings" h={56} fit="contain" />,
        monetizeScore: 9.2,
        usesMomentApi: true,
        accentColor: '#53d337',
    },
    {
        id: 'fanatics',
        name: 'Fanatics',
        logo: (
            <Text fw={900} size="3rem" c="white" style={{ lineHeight: 1 }}>
                F
            </Text>
        ),
        monetizeScore: 7.8,
        usesMomentApi: false,
        accentColor: '#e31837',
    },
    {
        id: 'ads',
        name: 'Ads',
        logo: (
            <ThemeIcon size={56} radius="xl" variant="filled" color="violet">
                <IconSpeakerphone size={28} stroke={1.5} />
            </ThemeIcon>
        ),
        monetizeScore: 6.5,
        usesMomentApi: false,
        accentColor: '#7c3aed',
    },
];

const ScoreColor = (score: number) => {
    if (score >= 9) return 'teal';
    if (score >= 7) return 'yellow';
    return 'orange';
};

const Marketplace: React.FC = () => (
    <Box>
        <Group align="center" gap="sm" mb="xs">
            <Title order={2}>Marketplace</Title>
            <Badge variant="light" color="gray" size="lg">
                Coming Soon
            </Badge>
        </Group>
        <Text c="dimmed" size="sm" mb="xl">
            Activate partner integrations to monetize your screen network.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {PARTNERS.map(partner => (
                <Card key={partner.id} shadow="sm" padding="lg" radius="md" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
                    <Card.Section
                        style={{
                            backgroundColor: partner.id === 'draftkings' ? '#fff' : partner.accentColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '32px 24px',
                            borderBottom: `3px solid ${partner.accentColor}`,
                            minHeight: 120,
                        }}
                    >
                        {partner.logo}
                    </Card.Section>

                    <Text fw={700} size="lg" mt="md">
                        {partner.name}
                    </Text>

                    <Stack gap="xs" mt="sm" mb="md">
                        <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                                Monetize Score
                            </Text>
                            <Badge color={ScoreColor(partner.monetizeScore)} variant="light">
                                {partner.monetizeScore} / 10
                            </Badge>
                        </Group>
                        <Progress
                            value={partner.monetizeScore * 10}
                            color={ScoreColor(partner.monetizeScore)}
                            size="sm"
                            radius="xl"
                        />

                        <Group justify="space-between" mt={4}>
                            <Text size="sm" c="dimmed">
                                Moment API
                            </Text>
                            {partner.usesMomentApi ? (
                                <Badge
                                    color="teal"
                                    variant="light"
                                    leftSection={<IconCheck size={11} />}
                                >
                                    Enabled
                                </Badge>
                            ) : (
                                <Badge
                                    color="gray"
                                    variant="light"
                                    leftSection={<IconX size={11} />}
                                >
                                    Not available
                                </Badge>
                            )}
                        </Group>
                    </Stack>

                    <Button
                        mt="auto"
                        fullWidth
                        disabled
                        leftSection={<IconBolt size={16} />}
                    >
                        Activate
                    </Button>
                </Card>
            ))}
        </SimpleGrid>
    </Box>
);

export default Marketplace;
